import { Injectable } from '@angular/core';
import { DocPrediction, documentTypeLabel, keywordPrediction } from './document-classify';
import { extractFeatures, PixelView } from './document-features';
import { classifyFeatures, FusionModel } from './fusion-model';

/** Where the trained model is served from (drop `model.json` into `public/`). */
const MODEL_URL = 'assets/brdocs/doc-model.json';

/**
 * Loads the offline-trained late-fusion model (`public/doc-model.json`) and
 * classifies captured documents with it, extracting the same PII-free features
 * in the browser as the trainer did. If no model is present it transparently
 * falls back to the keyword classifier, so the app works with or without a
 * trained model — drop one in and it lights up.
 */
@Injectable({ providedIn: 'root' })
export class DocModel {
  private model: FusionModel | null = null;
  private loadPromise?: Promise<FusionModel | null>;
  /** True once a model.json has been found and loaded. */
  available = false;

  /** Lazily fetch the model once; resolves to `null` when none is published. */
  load(): Promise<FusionModel | null> {
    this.loadPromise ??= fetch(MODEL_URL)
      .then((r) => (r.ok ? (r.json() as Promise<FusionModel>) : null))
      .catch(() => null)
      .then((m) => {
        this.model = m && Array.isArray(m.labels) ? m : null;
        this.available = !!this.model;
        return this.model;
      });
    return this.loadPromise;
  }

  /**
   * Classify a captured document from its **upright** feature image (the caller
   * crops + de-rotates so this matches how the model was trained) and OCR text.
   * Uses the trained model when one is published; otherwise — or on any failure,
   * or when no feature image is available — falls back to the keyword classifier.
   */
  async classify(image: PixelView | null, text: string): Promise<DocPrediction> {
    let model: FusionModel | null = null;
    try {
      model = await this.load();
    } catch {
      // fall through to keyword
    }
    if (!model || !image) return keywordPrediction(text);
    try {
      const res = classifyFeatures(model, extractFeatures(image, null, text));
      return {
        type: res.type,
        label: documentTypeLabel(res.type),
        confidence: res.prob >= 0.55 ? 'high' : res.prob >= 0.4 ? 'medium' : 'low',
        source: 'model',
        experts: res.experts,
      };
    } catch {
      return keywordPrediction(text);
    }
  }
}
